import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowRight, Sparkles, Award } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { HERO } from "../data/home-data";
import { Button } from "./ui/button";

export default function Hero() {
    const [idx, setIdx] = useState(0);
    const [isPaused, setIsPaused] = useState(false);
    const navigate = useNavigate();

    useEffect(() => {
        if (isPaused) return;
        const t = setInterval(
            () => setIdx((i) => (i + 1) % HERO.audienceCards.length),
            4500,
        );
        return () => clearInterval(t);
    }, [isPaused]);

    const card = HERO.audienceCards[idx];

    return (
        <section
            className="relative pb-20 overflow-hidden pt-32 sm:pt-40"
            data-testid="hero-section"
        >
            {/* Background grain + blob */}
            <div className="absolute top-10 -right-32 w-[600px] h-[600px] rounded-full blur-3xl opacity-50 bg-gradient-to-tr from-aiorange to-aipeach float-fast" />

            <div className="max-w-7xl mx-auto px-4 sm:px-8 relative z-10 grid lg:grid-cols-12 gap-10 items-center">
                <div className="lg:col-span-7">
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.6 }}
                        className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-aiblue/10 text-aiblue font-medium text-sm mb-6"
                        data-testid="hero-badge"
                    >
                        <Sparkles className="h-4 w-4" />
                        {HERO.badge}
                    </motion.div>

                    <motion.h1
                        initial={{ opacity: 0, y: 30 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.7, delay: 0.1 }}
                        className="font-display font-black tracking-tight text-5xl sm:text-6xl lg:text-7xl leading-[1.02]"
                        data-testid="hero-headline"
                    >
                        <span className="text-aiblue">India's first.</span>
                        <br />
                        <span className="bg-gradient-to-r from-[#0052FF] to-[#FF6B00] bg-clip-text text-transparent">
                            AI-powered B.Tech
                        </span>
                        <br />
                        <span className="text-aiorange">Ecosystem.</span>
                    </motion.h1>

                    <motion.p
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ delay: 0.3, duration: 0.6 }}
                        className="mt-6 text-base sm:text-lg font-bold text-black-600 max-w-xl"
                        data-testid="hero-subheadline"
                    >
                        {HERO.subheadline}
                    </motion.p>

                    <motion.div
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.5, duration: 0.6 }}
                        className="mt-8 flex flex-wrap gap-3 mb-3"
                    >
                        <Button
                            onClick={() => navigate("/waitlist?role=student")}
                            className="bg-aiblue hover:bg-blue-700 text-white rounded-full px-7 py-6 text-base font-semibold group cta-pulse"
                            data-testid="hero-cta-primary"
                        >
                            {HERO.ctaPrimary}
                            <ArrowRight className="ml-2 h-5 w-5 group-hover:translate-x-1 transition-transform" />
                        </Button>
                        <Button
                            variant="outline"
                            onClick={() => navigate("/waitlist?role=university")}
                            className="rounded-full px-7 py-6 text-base font-semibold border-2 hover:border-aiblue hover:text-aiblue"
                            data-testid="hero-cta-secondary"
                        >
                            {HERO.ctaSecondary}
                        </Button>
                    </motion.div>

                    {/* Credibility Banner — directly under CTAs */}
                    <motion.div
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.6, duration: 0.6 }}
                        className="mt-5 inline-flex items-center gap-3 pl-1.5 pr-5 py-1.5 rounded-full bg-white border border-gray-200 shadow-lg shadow-blue-100/60"
                        data-testid="hero-credibility-banner"
                    >
                        <div className="flex items-center justify-center h-8 w-8 rounded-full bg-gradient-to-br from-aiblue to-purple-600 shrink-0">
                            <Award className="h-4 w-4 text-white" />
                        </div>
                        <p className="text-sm font-semibold text-gray-700 whitespace-nowrap">
                            Architected by{" "}
                            <span className="text-aiblue font-bold">CMU Alumni</span>
                            {" "}&amp;{" "}Co-Founder of a{" "}
                            <span className="text-aiorange font-bold">$5.3B AI Unicorn</span>
                        </p>
                    </motion.div>

                    <div
                        className="mt-8 flex items-center gap-6 text-sm text-gray-500"
                        data-testid="hero-meta"
                    >
                        <div className="flex -space-x-2">
                            {HERO.audienceCards.map((c, i) => (
                                <div
                                    key={i}
                                    className="h-8 w-8 rounded-full border-2 border-white overflow-hidden bg-gray-100"
                                >
                                    <img
                                        src={c.image}
                                        alt=""
                                        className="h-full w-full object-cover"
                                    />
                                </div>
                            ))}
                        </div>
                        <span>{HERO.meta}</span>
                    </div>

                </div>

                {/* Right - rotating audience card */}
                <div
                    className="lg:col-span-5 relative h-[500px]"
                    data-testid="hero-audience-carousel"
                    onMouseEnter={() => setIsPaused(true)}
                    onMouseLeave={() => {
                        setIsPaused(false);
                        setIdx((i) => (i + 1) % HERO.audienceCards.length);
                    }}
                >
                    <AnimatePresence mode="wait">
                        <motion.div
                            key={card.key}
                            initial={{ opacity: 0, scale: 0.9, rotate: -4 }}
                            animate={{ opacity: 1, scale: 1, rotate: 0 }}
                            exit={{ opacity: 0, scale: 0.9, rotate: 4 }}
                            transition={{
                                duration: 0.5,
                                ease: [0.22, 1, 0.36, 1],
                            }}
                            className={`absolute inset-0 rounded-[2rem] overflow-hidden shadow-2xl bg-gradient-to-br ${card.accent}`}
                            data-testid={`audience-card-${card.key}`}
                        >
                            <img
                                src={card.image}
                                alt={card.tag}
                                className="absolute inset-0 w-full h-full object-cover opacity-60"
                            />
                            <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent" />
                            <div className="relative h-full flex flex-col justify-end p-8 text-white">
                                <span className="text-xs uppercase tracking-widest font-bold opacity-80 mb-2">
                                    {card.tag}
                                </span>
                                <h3 className="font-display text-3xl font-bold leading-tight mb-3">
                                    {card.headline}
                                </h3>
                                <p className="text-sm opacity-90 mb-5 max-w-md">
                                    {card.subcopy}
                                </p>
                                <button
                                    onClick={() =>
                                        navigate(`/waitlist?role=${card.key}`)
                                    }
                                    className="self-start inline-flex items-center gap-2 px-5 py-3 bg-white text-black rounded-full font-semibold text-sm hover:bg-aiorange hover:text-white transition-colors"
                                    data-testid={`audience-cta-${card.key}`}
                                >
                                    {card.cta}{" "}
                                    <ArrowRight className="h-4 w-4" />
                                </button>
                            </div>
                        </motion.div>
                    </AnimatePresence>

                    {/* Pagination dots */}
                    <div className="absolute -bottom-8 left-0 right-0 flex justify-center gap-2 z-10">
                        {HERO.audienceCards.map((_, i) => (
                            <button
                                key={i}
                                onClick={() => setIdx(i)}
                                className={`h-2 rounded-full transition-all ${i === idx ? "w-8 bg-aiblue" : "w-2 bg-gray-300"}`}
                                data-testid={`hero-dot-${i}`}
                            />
                        ))}
                    </div>
                </div>
            </div>
        </section>
    );
}
